import { observable, action, makeObservable, runInAction } from 'mobx'
import KdsAuthorizationStore from './KdsAuthorizationStore'
import { recordLockState } from '../Services/DeviceService'
import { KdsActionId } from '../Types/KdsAuthorization'
import { KdsAuthorizationCancelledError } from '../Types/Authorization'
import { isActionElevated } from '../Utils/kdsActionPolicy'
import GlobalStore from './GlobalStore'

const LOCK_STORAGE_KEY = 'kdsLocked'

function readPersistedLock(): boolean {
  try {
    return localStorage.getItem(LOCK_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

class KdsLockStore {
  isLocked = readPersistedLock()

  constructor() {
    makeObservable(this, {
      isLocked: observable,
      lock: action.bound,
      unlock: action.bound,
    })
  }

  async lock(): Promise<boolean> {
    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.LockKds,
        () => recordLockState(true),
        authorization => {
          // Policy may have changed while the PIN prompt was open; re-validate before invoking.
          const currentPolicy = GlobalStore.configFetch.data?.actionPolicy
          if (currentPolicy && !isActionElevated(currentPolicy, KdsActionId.LockKds)) {
            throw new Error(`Action is no longer available: ${KdsActionId.LockKds}`)
          }
          return recordLockState(true, authorization.token)
        }
      )

      localStorage.setItem(LOCK_STORAGE_KEY, 'true')
      runInAction(() => {
        this.isLocked = true
      })
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      throw error
    }
  }

  async unlock(): Promise<boolean> {
    try {
      await KdsAuthorizationStore.runConfiguredAction(
        KdsActionId.LockKds,
        () => recordLockState(false),
        authorization => {
          // Policy may have changed while the PIN prompt was open; re-validate before invoking.
          const currentPolicy = GlobalStore.configFetch.data?.actionPolicy
          if (currentPolicy && !isActionElevated(currentPolicy, KdsActionId.LockKds)) {
            throw new Error(`Action is no longer available: ${KdsActionId.LockKds}`)
          }
          return recordLockState(false, authorization.token)
        }
      )

      localStorage.removeItem(LOCK_STORAGE_KEY)
      runInAction(() => {
        this.isLocked = false
      })
      return true
    } catch (error) {
      if (error instanceof KdsAuthorizationCancelledError) {
        return false
      }
      throw error
    }
  }
}

const kdsLockStore = new KdsLockStore()
export default kdsLockStore
export { KdsLockStore }
